import {Injectable} from "@angular/core";
import { Event } from "../model/interface/event";
import { EventNode } from "../model/interface/event-node";
import { Product } from "../model";
import { forkJoin, from, mergeMap, Observable, of, retry, Subject } from "rxjs";
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { UserService } from "@/app/services/user.service";
import { environment } from "@/environments/environment";
import { catchError, map } from "rxjs/operators";
import { flattenDisplayLabel } from "@/app/services/format.service";
import { Registration } from "@/app/model/interface/registration";
import { CookieService } from "@/app/services/cookie.service";
import { filter, uniqBy, map as _map } from "lodash-es";

@Injectable({
    providedIn: 'root'
})
export class DispatchScheduleService {

    eps_url: string;
    ems_url: string;
    events: Event[];
    params: HttpParams;
    headers: HttpHeaders;
    eventNodes$ = new Subject<EventNode[]>();
    productsMap: {[key: string]: Product} = {};
    eventsMap: Map<string, Event> = new Map<string, Event>();
    registrationsMap: Map<string, Registration> = new Map<string, Registration>();
    allEventNodes: EventNode[] = [];
    allowedEventTypes = ['OFFICIAL', 'AUDIT', 'VOLUNTARY', 'READINESS_TEST', 'STANDBY'];

    constructor(
        private http: HttpClient,
        private cookieService: CookieService,
        private userService: UserService
    ) {
        this.ems_url = environment.emsUrl;
        this.eps_url = environment.baseUrl;
        const session = this.cookieService.getCookie('enoc_session');
        this.headers = new HttpHeaders().append('enoc_session', session);
        this.params = new HttpParams().set('hierarchy', 'true');
    }

    getEvents(){
        this.allEventNodes = [];
        this.eventsMap.clear();
        this.http.get<any>(`${this.ems_url}/v1/eps_events`, { params: {hierarchy: true, show_all_event_types: true, withWorkflow:true}, withCredentials:true }).pipe(retry(2), map(({data}) => data)).subscribe(
            async (resp: Event[])=>{
                this.events = filter(resp, (event)=>{
                    return this.allowedEventTypes.includes(event.event_action_type)
                })
                
                const locale = this.cookieService.getI18NLocale() || 'en_US';
                this.events.forEach((event: Event)=>{
                    event.program_name = flattenDisplayLabel(locale, event.program_name);
                    this.eventsMap.set(event.event_id, event);
                    this.allEventNodes = [...this.allEventNodes, ...event.event_nodes]
                })
                
                await this.getProducts();
            },
            (error)=>{
                console.error('Error fetching events:', error);
                this.eventNodes$.next([]);
            }
        )
    }

    async getProducts(){
        const locale = this.cookieService.getI18NLocale() || 'en_US';
        const uniqueProdIds: Observable<any[]> = of((_map(uniqBy(this.events, 'product_id'), 'product_id')));
        
        return new Promise<void>((resolve, reject) => {
            uniqueProdIds.pipe(
                mergeMap((ids)=>{
                    if(!ids.length) {
                        return of<any[]>([]);
                    }
                    return forkJoin(
                        ids.map((id) => {
                                if(id) {
                                    if(this.productsMap[id]) {
                                        return from(new Promise((resolve) => resolve(this.productsMap[id]))) ;
                                    } else {
                                        return this.http.get<any>(`${this.ems_url}/v1/product/${id}`, {withCredentials: true}).pipe(
                                            map(({data}) => data),
                                            map((resp: Product) => {
                                                this.productsMap[id] = resp;
                                                return resp;
                                            }),
                                            catchError((err)=> {
                                                console.error('Error fetching product:', err);
                                                return of(null);
                                            }),
                                        );
                                    }
                                } else {
                                    return of(null);
                                }
                            },
                        ));
                })
            ).subscribe({
                next: (resp) => {
                    this.events.forEach((event)=>{
                        event.product = this.productsMap[event.product_id]
                    })
                    this.allEventNodes.forEach((node: EventNode)=>{
                        node.event = <Event>this.eventsMap.get(node.event_id);
                        node.locale = locale;
                    })
                    this.eventNodes$.next(this.allEventNodes);
                    resolve();
                },
                error: (error) => {
                    console.error('Error in getProducts:', error);
                    this.eventNodes$.next([]);
                    reject(error);
                }
            });
        });
    }

    getRegistrations(){
        const uniqueProdIds: Observable<any[]> = of((_map(uniqBy(this.events, 'product_id'), 'product_id')));

        uniqueProdIds.pipe(
            mergeMap((ids)=>{
                if(!ids.length) {
                    return of<any[]>([]);
                }
                return forkJoin(
                    ids.map((id) => {
                            return this.http.get<any>(`${this.ems_url}/v1/products/${id}/registrations?statuses=ACTIVE`, {withCredentials: true}).pipe(
                                map(({data}) => data),
                                map((resp: Registration[]) => {
                                    resp.forEach((reg)=>{
                                        this.registrationsMap.set(reg.id, reg);
                                    })

                                    return resp;
                                }),
                                catchError((err)=> {
                                    console.error('Error fetching registrations:', err);
                                    return of([]);
                                }),
                            );
                        },
                    ));
            })
        ).subscribe(
            async (resp)=>{
                for (const node of this.allEventNodes) {
                    node.registration = <Registration>this.registrationsMap.get(node.registration_id);
                    node.event = <Event>this.eventsMap.get(node.event_id);
                    node.locale = this.cookieService.getI18NLocale() || 'en_US';
                }
                //this.groupNodes();
                this.eventNodes$.next(this.allEventNodes)
            }
        )
    }

    clear(){
        this.eventsMap.clear();
        this.allEventNodes = [];
        this.productsMap = {};
    }
}